/**
 * Canonical action icons for table rows + buttons.
 *
 * Single source of truth for "View", "Edit", "Delete", "Add" and other
 * recurring action icons. Always import from here — do not pull from
 * lucide-react directly in feature code, otherwise we drift back to
 * inconsistent icon styling.
 *
 * Each icon is a thin wrapper over the lucide source so we can swap the
 * underlying icon or default size/stroke in one place.
 */

import {
  ArrowDownToLine,
  ArrowUpToLine,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleCheck,
  CircleX,
  Copy,
  EyeOff,
  Filter,
  History,
  Info,
  HelpCircle,
  MoreHorizontal,
  MoreVertical,
  RefreshCw,
  Search,
  Settings as SettingsLucide,
  Share2,
  Eye,
  FileText,
  Pencil,
  PencilLine,
  Trash2,
  Plus,
  UserCheck,
  X,
  type LucideProps,
  type LucideIcon,
} from "lucide-react";
import { forwardRef } from "react";

const make = (Lucide: LucideIcon): LucideIcon =>
  forwardRef<SVGSVGElement, LucideProps>(
    ({ className = "size-4", ...rest }, ref) => (
      <Lucide ref={ref} className={className} {...rest} />
    )
  ) as LucideIcon;

// Canonical table actions
export const ViewIcon     = make(Eye);
export const DocumentIcon = make(FileText);
export const EditIcon = make(Pencil);
export const DeleteIcon = make(Trash2);
export const AddIcon = make(Plus);
export const ReboardIcon = make(UserCheck);
// Request decisions (approve / reject / send back for changes)
export const ApproveIcon = make(CircleCheck);
export const RejectIcon = make(CircleX);
export const RequestChangesIcon = make(PencilLine);

// Common secondary actions
export const DownloadIcon = make(ArrowDownToLine);
export const UploadIcon = make(ArrowUpToLine);
export const HistoryIcon = make(History);
export const CopyIcon = make(Copy);
export const ShareIcon = make(Share2);
export const RefreshIcon = make(RefreshCw);
export const SearchIcon = make(Search);
export const FilterIcon = make(Filter);
export const InfoIcon = make(Info);
export const HelpIcon = make(HelpCircle);
export const SettingsIcon = make(SettingsLucide);
export const CloseIcon = make(X);
export const CheckIcon = make(Check);
export const HideIcon = make(EyeOff);
export const MoreIcon = make(MoreHorizontal);
export const MoreVIcon = make(MoreVertical);
export const ChevronUpIcon = make(ChevronUp);
export const ChevronDownIcon = make(ChevronDown);
export const ChevronLeftIcon = make(ChevronLeft);
export const ChevronRightIcon = make(ChevronRight);
